import { useState } from "react";
import {
  Surface,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Badge,
  Kbd,
  Code,
  SegmentedControl,
  type SurfaceVariant,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

const SURFACES: {
  variant: SurfaceVariant;
  name: string;
  description: string;
  features: string;
}[] = [
  {
    variant: "flat",
    name: "Affleurant",
    description: "Neutre, sans relief, alignée avec le fond de page.",
    features: "bg-surface · border-border/50 · Pas d'ombre",
  },
  {
    variant: "raised",
    name: "Surélevé",
    description: "Arête spéculaire supérieure et ombre minimale.",
    features: "bg-surface-raised · inset-shadow-raised · shadow-xs",
  },
  {
    variant: "sunken",
    name: "Enfoncé",
    description: "Zones de saisie, blocs de code, pistes de contrôles.",
    features: "bg-surface-sunken · inset-shadow-control",
  },
  {
    variant: "glass",
    name: "Verre dépoli",
    description: "Flou d'arrière-plan posé une fois, jamais interpolé.",
    features: "backdrop-blur-md · bg-surface-raised/80 · inset-shadow-glass",
  },
  {
    variant: "floating",
    name: "Flottant",
    description: "Menus déroulants, popovers, infobulles.",
    features: "bg-surface-raised · shadow-md · border-border",
  },
  {
    variant: "overlay",
    name: "Surcouche",
    description: "Modales, dialogs, panneaux de surcouche.",
    features: "bg-surface-raised · shadow-overlay · inset-shadow-raised",
  },
  {
    variant: "control",
    name: "Contrôle",
    description: "Boutons, champs, segments.",
    features: "bg-surface-raised · border-border-strong · inset-shadow-raised",
  },
  {
    variant: "subtle",
    name: "Teinté marque",
    description: "Notifications, chips, onglets actifs.",
    features: "bg-accent-subtle · text-accent-fg · border-accent/20",
  },
];

export function ComponentSurfacesSection() {
  const [selectedVariant, setSelectedVariant] = useState<SurfaceVariant>("glass");

  return (
    <Section
      id="surfaces-composants"
      eyebrow="Fondations"
      title="Surfaces"
      description="La surface de page (--nx-color-surface) et la surface de composant sont deux choses distinctes. Chaque matériau porte sa propre profondeur et sa propre arête spéculaire."
    >
      <SubSection
        title="Matériaux"
        hint="Survolez : l'ombre est un calque en fondu, pas une ombre interpolée"
      >
        <Preview
          layout="stack"
          label="surface.tsx"
          code={`<Surface variant="glass">Contenu translucide</Surface>
<Surface variant="raised" interactive onClick={select}>Carte cliquable</Surface>`}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SURFACES.map((s) => (
            <Surface
              key={s.variant}
              variant={s.variant}
              interactive
              onClick={() => setSelectedVariant(s.variant)}
              className="flex flex-col justify-between rounded-xl p-5"
            >
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold uppercase">{s.variant}</span>
                  {selectedVariant === s.variant && (
                    <Badge tone="brand" size="sm">
                      Sélectionné
                    </Badge>
                  )}
                </div>
                <h4 className="mt-2 text-sm font-semibold text-fg">{s.name}</h4>
                <p className="mt-1 text-xs text-fg-muted">{s.description}</p>
              </div>

              <div className="mt-4 pt-3 border-t border-border/40 font-mono text-[10px] text-fg-muted">
                {s.features}
              </div>
            </Surface>
            ))}
          </div>
        </Preview>
      </SubSection>

      <SubSection title="Aperçu" hint="Le conteneur suit le matériau sélectionné">
        <div className="relative overflow-hidden rounded-2xl border border-border p-8 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent-subtle/30 via-transparent to-transparent">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h4 className="text-base font-semibold text-fg">Sélecteur de surface</h4>
            <SegmentedControl
              aria-label="Variante de surface"
              size="sm"
              value={selectedVariant}
              onValueChange={(val) => setSelectedVariant(val as SurfaceVariant)}
              options={[
                { value: "glass", label: "Glass" },
                { value: "raised", label: "Raised" },
                { value: "sunken", label: "Sunken" },
                { value: "floating", label: "Floating" },
                { value: "subtle", label: "Subtle" },
              ]}
            />
          </div>

          {/* Rendu dynamique du composant Surface */}
          <Surface variant={selectedVariant} className="p-6 rounded-xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-accent animate-pulse" />
                <span className="font-mono text-xs font-semibold text-fg uppercase">
                  Surface: {selectedVariant}
                </span>
              </div>
              <Kbd>⌘ + S</Kbd>
            </div>

            <p className="text-sm text-fg">
              Surface <Code>{selectedVariant}</Code> — contraste WCAG AA, arête spéculaire et
              translucidité adaptative en thème sombre viennent avec.
            </p>

            <div className="flex items-center gap-3 pt-2">
              <Button variant="primary" size="sm">
                Action principale
              </Button>
              <Button variant="outline" size="sm">
                Secondaire
              </Button>
            </div>
          </Surface>
        </div>
      </SubSection>

      <SubSection title="Dans les composants">
        <Preview
          layout="stack"
          label="cards.tsx"
          code={`<Card surface="glass">…</Card>
<Card surface="raised" interactive>…</Card>
<Card surface="sunken">…</Card>`}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card surface="glass" className="flex flex-col justify-between">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Verre dépoli</CardTitle>
                <Badge tone="brand">glass</Badge>
              </div>
              <CardDescription>Flou natif et bordure spéculaire.</CardDescription>
            </CardHeader>
            <CardBody className="text-xs text-fg-muted">
              <p>Arête spéculaire supérieure, en thème clair comme en sombre.</p>
            </CardBody>
          </Card>

          <Card surface="raised" interactive className="flex flex-col justify-between">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Surélevé</CardTitle>
                <Badge tone="neutral">raised</Badge>
              </div>
              <CardDescription>Soulèvement d'un pixel au survol.</CardDescription>
            </CardHeader>
            <CardBody className="text-xs text-fg-muted">
              <p>Survolez pour observer le relief.</p>
            </CardBody>
          </Card>

          <Card surface="sunken" className="flex flex-col justify-between">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Enfoncé</CardTitle>
                <Badge tone="info">sunken</Badge>
              </div>
              <CardDescription>Zones de saisie et blocs de code.</CardDescription>
            </CardHeader>
            <CardBody className="text-xs text-fg-muted">
              <Code>inset-shadow-control</Code> creuse la surface supérieure.
            </CardBody>
          </Card>
          </div>
        </Preview>
      </SubSection>
    </Section>
  );
}
